import './AllergyList.css'
import type { Allergy } from '../types/allergy'
import { formatDate } from '../utils/locale'

interface PatientNkdaBannerProps {
  allergies: Allergy[]
  onSetNkda: (flag: boolean, reason?: string) => Promise<void> | void
  readOnly?: boolean
}

export function PatientNkdaBanner({ allergies, onSetNkda, readOnly = false }: PatientNkdaBannerProps) {
  const nkdaRecord = allergies.find((a) => a.isNKDA && a.status === 'active')
  const hasNkda = !!nkdaRecord

  const handleToggle = async () => {
    if (readOnly) {
      return
    }
    if (hasNkda) {
      const confirm = window.confirm(
        'Clear NKDA? This indicates that the patient may have drug allergies that need to be recorded explicitly.',
      )
      if (!confirm) return
      await onSetNkda(false, 'NKDA cleared')
    } else {
      const confirm = window.confirm(
        'Set NKDA (No Known Drug Allergies)? This indicates that, to the best of your knowledge, the patient has no drug allergies.',
      )
      if (!confirm) return
      await onSetNkda(true, 'NKDA recorded')
    }
  }

  return (
    <div className={`nkda-patient-banner ${hasNkda ? 'nkda-present' : 'nkda-absent'}`}>
      <div className="nkda-banner-content">
        <div className="nkda-banner-label">
          <span className="nkda-chip">{hasNkda ? 'NKDA' : 'Drug Allergy Status'}</span>
          <span className="nkda-banner-title">
            {hasNkda ? 'No known drug allergies recorded' : 'Drug allergy status not confirmed'}
          </span>
        </div>
        <div className="nkda-banner-meta">
          {hasNkda ? (
            <>
              <span>
                Recorded{' '}
                {nkdaRecord?.recordedDate
                  ? formatDate(nkdaRecord.recordedDate)
                  : formatDate(nkdaRecord?.createdAt || '')}
              </span>
              {nkdaRecord?.updatedBy && <span> • by {nkdaRecord.updatedBy}</span>}
            </>
          ) : (
            <span>
              NKDA applies to the patient as a whole, not individual reactions. Review before prescribing.
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className={`nkda-toggle-button ${hasNkda ? 'clear' : 'set'}`}
        onClick={handleToggle}
        disabled={readOnly}
        title={readOnly ? 'Secondary records are read-only' : undefined}
      >
        {hasNkda ? 'Clear NKDA' : 'Set NKDA'}
      </button>
    </div>
  )
}

